(() => {
  'use strict';

  const parseJSON = (node, fallback) => {
    if (!node?.textContent) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return fallback;
    }
  };

  const configNode = document.querySelector('[data-cc-config]');
  const config = parseJSON(configNode, {});
  const body = document.body;
  const moneyFallback = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: config.currency || 'USD',
  });

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const escapeHTML = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    })[character]);

  const displayTitle = (value = '') => String(value).split('–')[0].trim();

  const initializeHoverImages = (scope = document) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const images = [
      ...(scope.matches?.('[data-cc-hover-src]') ? [scope] : []),
      ...$$('[data-cc-hover-src]', scope),
    ];

    images.forEach((image) => {
      image.srcset = image.dataset.ccHoverSrcset || '';
      image.sizes = image.dataset.ccHoverSizes || '';
      image.src = image.dataset.ccHoverSrc;
      delete image.dataset.ccHoverSrc;
      delete image.dataset.ccHoverSrcset;
      delete image.dataset.ccHoverSizes;
    });
  };

  const formatWithDelimiters = (cents, precision = 2, thousands = ',', decimal = '.') => {
    const amount = (Number(cents || 0) / 100).toFixed(precision);
    const [whole, fraction] = amount.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction === undefined ? grouped : `${grouped}${decimal}${fraction}`;
  };

  const formatMoney = (cents = 0) => {
    const format = String(config.moneyFormat || '').replace(/&nbsp;/g, '\u00a0');
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return moneyFallback.format(Number(cents) / 100);

    const token = placeholder[1];
    const formatted = {
      amount: () => formatWithDelimiters(cents, 2, ',', '.'),
      amount_no_decimals: () => formatWithDelimiters(cents, 0, ',', '.'),
      amount_with_comma_separator: () => formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => formatWithDelimiters(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => formatWithDelimiters(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => formatWithDelimiters(cents, 0, ' ', '.'),
      amount_with_space_separator: () => formatWithDelimiters(cents, 2, ' ', ','),
    }[token];

    return formatted ? format.replace(placeholder[0], formatted()) : moneyFallback.format(Number(cents) / 100);
  };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const networkConnection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const prepareSilentInlineVideo = (video, { loop = false, controls } = {}) => {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = loop;
    video.playsInline = true;
    if (typeof controls === 'boolean') video.controls = controls;
  };

  /* Contextual frosted header */
  let destroyHeroHeader = () => {};
  const initializeHeroHeader = () => {
    destroyHeroHeader();
    const heroHeader = $('[data-hero-header]');
    const cinematicHero = $('.cc-cinematic-hero, .cc-home-hero, .cc-collection-hero');
    if (!heroHeader) return;
    if (!cinematicHero) {
      heroHeader.classList.add('is-solid');
      return;
    }

    const syncHeroHeader = () => {
      const heroRect = cinematicHero.getBoundingClientRect();
      const headerBottom = heroHeader.getBoundingClientRect().bottom;
      heroHeader.classList.toggle('is-solid', heroRect.bottom <= headerBottom + 1);
    };
    let syncFrame = 0;
    const scheduleHeroHeaderSync = () => {
      if (syncFrame) return;
      syncFrame = requestAnimationFrame(() => {
        syncFrame = 0;
        syncHeroHeader();
      });
    };
    const resyncHeroHeader = () => {
      scheduleHeroHeaderSync();
      requestAnimationFrame(scheduleHeroHeaderSync);
    };

    syncHeroHeader();
    window.addEventListener('scroll', scheduleHeroHeaderSync, { passive: true });
    window.addEventListener('resize', scheduleHeroHeaderSync, { passive: true });
    window.addEventListener('pageshow', resyncHeroHeader, { passive: true });
    window.visualViewport?.addEventListener('resize', scheduleHeroHeaderSync, { passive: true });
    window.visualViewport?.addEventListener('scroll', scheduleHeroHeaderSync, { passive: true });
    destroyHeroHeader = () => {
      if (syncFrame) cancelAnimationFrame(syncFrame);
      window.removeEventListener('scroll', scheduleHeroHeaderSync);
      window.removeEventListener('resize', scheduleHeroHeaderSync);
      window.removeEventListener('pageshow', resyncHeroHeader);
      window.visualViewport?.removeEventListener('resize', scheduleHeroHeaderSync);
      window.visualViewport?.removeEventListener('scroll', scheduleHeroHeaderSync);
    };
  };

  const freeShippingEnabled = Boolean(config.freeShippingEnabled);
  const freeShippingThreshold = Number(config.freeShippingThreshold || 5000);
  let observedCartTotal = Number(config.cartTotal || 0);

  const shippingMessage = (total) => {
    const remaining = Math.max(0, freeShippingThreshold - Number(total || 0));
    return remaining > 0
      ? `Add ${formatMoney(remaining)} for free shipping.`
      : (config.freeShippingUnlockedText || 'Free shipping unlocked.');
  };

  const celebrateFreeShipping = () => {
    const announcer = $('[data-shipping-announcer]');
    const message = config.freeShippingUnlockedText || 'Free shipping unlocked.';
    if (announcer) {
      announcer.textContent = '';
      requestAnimationFrame(() => { announcer.textContent = message; });
    }

    if (reducedMotion.matches) return;
    const celebration = $('[data-shipping-celebration]');
    const starField = $('[data-shipping-stars]', celebration);
    if (!celebration || !starField) return;

    const colors = ['var(--cc-purple)', 'var(--cc-red)', 'var(--cc-yellow)', 'var(--cc-blue)', 'var(--cc-plum)'];
    const stars = Array.from({ length: 28 }, (_, index) => {
      const x = 4 + ((index * 37) % 92);
      const y = 4 + ((index * 53) % 88);
      const size = 20 + ((index * 17) % 42);
      const delay = (index % 7) * 45;
      const rotation = (index * 29) % 180;
      const color = colors[index % colors.length];
      return `<span class="cc-shipping-star" style="--cc-star-x:${x}%;--cc-star-y:${y}%;--cc-star-size:${size}px;--cc-star-delay:${delay}ms;--cc-star-rotation:${rotation}deg;--cc-star-color:${color}">✦</span>`;
    }).join('');

    starField.innerHTML = stars;
    celebration.hidden = false;
    celebration.classList.remove('is-active');
    void celebration.offsetWidth;
    celebration.classList.add('is-active');
    clearTimeout(celebrateFreeShipping.timeout);
    celebrateFreeShipping.timeout = setTimeout(() => {
      celebration.classList.remove('is-active');
      celebration.hidden = true;
      starField.innerHTML = '';
    }, 1900);
  };

  const syncShippingProgress = (newTotal, { mutation = false } = {}) => {
    if (!freeShippingEnabled || freeShippingThreshold <= 0) return;
    const previousTotal = observedCartTotal;
    const total = Math.max(0, Number(newTotal || 0));
    const remaining = Math.max(0, freeShippingThreshold - total);
    const percentage = Math.min(100, (total / freeShippingThreshold) * 100);
    const unlocked = remaining === 0;
    observedCartTotal = total;

    $$('[data-shipping-progress]').forEach((progress) => {
      progress.classList.toggle('is-unlocked', unlocked);
      const messageNode = $('[data-shipping-message]', progress);
      if (messageNode) {
        messageNode.innerHTML = unlocked
          ? `<strong>${escapeHTML(config.freeShippingUnlockedText || 'Free shipping unlocked.')}</strong>`
          : `Add <strong>${escapeHTML(formatMoney(remaining))}</strong> for free shipping.`;
      }
      const valueNode = $('[data-shipping-value]', progress);
      if (valueNode) valueNode.textContent = `${formatMoney(total)} / ${formatMoney(freeShippingThreshold)}`;
      const track = $('[data-shipping-track]', progress);
      const fill = $('[data-shipping-fill]', progress);
      if (fill) fill.style.setProperty('--cc-shipping-progress', `${percentage}%`);
      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(percentage)));
        track.setAttribute('aria-valuetext', unlocked ? shippingMessage(total) : `${formatMoney(remaining)} remaining for free shipping`);
      }
    });

    if (mutation && previousTotal < freeShippingThreshold && total >= freeShippingThreshold) {
      celebrateFreeShipping();
    }
  };

  const setBodyLock = () => {
    const openLayer = document.querySelector('.cc-menu-layer:not([hidden]), .cc-drawer-layer:not([hidden])');
    body.classList.toggle('is-locked', Boolean(openLayer));
  };

  const getFocusable = (container) => {
    if (!container) return [];
    return $$(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      container
    ).filter((element) => {
      if (element.matches('.cc-menu-scrim, .cc-drawer-scrim')) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  };

  const focusFirst = (container) => {
    getFocusable(container)[0]?.focus({ preventScroll: true });
  };

  const isVisible = (element) =>
    Boolean(element?.isConnected)
    && !element.closest('[hidden]')
    && element.getClientRects().length > 0
    && window.getComputedStyle(element).visibility !== 'hidden';

  const isOnscreen = (element) => {
    if (!isVisible(element)) return false;
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
  };

  const showToast = (message) => {
    const toast = $('[data-toast]');
    if (!toast) return;
    const cartIsOpen = Boolean(cartLayer && !cartLayer.hidden);
    toast.classList.toggle('is-cart-toast', cartIsOpen);
    toast.style.removeProperty('--cc-toast-bottom');
    if (cartIsOpen) {
      const summary = $('.cc-cart-summary', cartLayer);
      if (summary) {
        const summaryTop = summary.getBoundingClientRect().top;
        const bottomOffset = Math.max(22, window.innerHeight - summaryTop + 12);
        toast.style.setProperty('--cc-toast-bottom', `${Math.round(bottomOffset)}px`);
      }
    }
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(showToast.timeout);
    showToast.timeout = setTimeout(() => {
      toast.hidden = true;
      toast.classList.remove('is-cart-toast');
      toast.style.removeProperty('--cc-toast-bottom');
    }, 1900);
  };

  /* Product cards remain fully tappable outside their explicit links. */
  document.addEventListener('click', (event) => {
    const card = event.target.closest('.cc-product-card');
    if (!card || event.target.closest('a, button, input, select, textarea, summary')) return;
    const destination = $('.cc-product-card__title', card) || $('.cc-product-card__media', card);
    if (destination?.href) window.location.assign(destination.href);
  });

  /* Menu and search */
  let menuLayer = $('[data-menu-layer]');
  let searchPanel = $('[data-search-panel]');
  let menuOpener = null;
  let searchOpener = null;

  const openMenu = (event) => {
    if (!menuLayer) return;
    menuOpener = event?.currentTarget || document.activeElement;
    menuLayer.hidden = false;
    setBodyLock();
    requestAnimationFrame(() => focusFirst($('[role="dialog"]', menuLayer) || menuLayer));
  };

  const closeMenu = ({ restoreFocus = true } = {}) => {
    if (!menuLayer) return;
    menuLayer.hidden = true;
    setBodyLock();
    if (restoreFocus) {
      const fallback = $$('[data-menu-open], [data-search-open], .cc-logo').find(isVisible);
      const target = isVisible(menuOpener) ? menuOpener : fallback;
      target?.focus({ preventScroll: true });
    }
    menuOpener = null;
  };

  const openSearch = (event) => {
    searchOpener = event?.currentTarget || document.activeElement;
    closeMenu({ restoreFocus: false });
    if (!searchPanel) return;
    searchPanel.hidden = false;
    requestAnimationFrame(() => $('[data-search-input]', searchPanel)?.focus());
  };

  const closeSearch = () => {
    if (!searchPanel) return;
    searchPanel.hidden = true;
    const fallback = $$('[data-search-open], [data-menu-open]').find(isVisible);
    const target = isVisible(searchOpener) ? searchOpener : fallback;
    target?.focus({ preventScroll: true });
    searchOpener = null;
  };

  document.addEventListener('click', (event) => {
    const menuOpen = event.target.closest('[data-menu-open]');
    if (menuOpen) {
      openMenu({ currentTarget: menuOpen });
      return;
    }
    if (event.target.closest('[data-menu-close]')) {
      closeMenu();
      return;
    }
    const searchOpen = event.target.closest('[data-search-open]');
    if (searchOpen) {
      openSearch({ currentTarget: searchOpen });
      return;
    }
    if (event.target.closest('[data-search-close]')) closeSearch();
  });

  /* Cart */
  const cartLayer = $('[data-cart-layer]');
  const cartBody = $('[data-cart-drawer-body]');
  let cartOpener = null;
  const defaultDiscountCode = String(config.discountCode || 'CAKE10').trim().toUpperCase();
  const discountThreshold = Math.max(0, Number(config.discountThreshold || 3500));
  const discountCodeStorageKey = 'cc:discount-code';
  const discountFeedbackStorageKey = 'cc:discount-feedback';

  const readSessionValue = (key) => {
    try {
      return sessionStorage.getItem(key) || '';
    } catch {
      return '';
    }
  };

  const writeSessionValue = (key, value) => {
    try {
      if (value) sessionStorage.setItem(key, value);
      else sessionStorage.removeItem(key);
    } catch {
      /* Storage can be unavailable in strict privacy modes. */
    }
  };

  let activeDiscountCode = readSessionValue(discountCodeStorageKey).trim().toUpperCase();
  let attemptedDiscountCode = activeDiscountCode;
  let discountFeedback = readSessionValue(discountFeedbackStorageKey);
  writeSessionValue(discountFeedbackStorageKey, '');

  const cartPreDiscountSubtotal = (cart) => Number(
    cart?.items_subtotal_price
    ?? cart?.original_total_price
    ?? cart?.total_price
    ?? 0
  );

  const cartDiscountRecords = (cart) => Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
  const cartDiscountApplications = (cart) => Array.isArray(cart?.cart_level_discount_applications)
    ? cart.cart_level_discount_applications
    : [];

  const discountIsApplied = (cart, code) => {
    const normalizedCode = String(code || '').trim().toUpperCase();
    if (!normalizedCode) return false;
    const record = cartDiscountRecords(cart).find((discount) =>
      String(discount.code || '').trim().toUpperCase() === normalizedCode
    );
    if (record) return record.applicable !== false;
    return cartDiscountApplications(cart).some((discount) =>
      String(discount.title || '').trim().toUpperCase() === normalizedCode
    );
  };

  const discountSavings = (cart, code) => {
    const normalizedCode = String(code || '').trim().toUpperCase();
    return cartDiscountApplications(cart)
      .filter((discount) => String(discount.title || '').trim().toUpperCase() === normalizedCode)
      .reduce((total, discount) => total + Number(discount.total_allocated_amount || 0), 0);
  };

  const discountStatusForCart = (cart, code, { explicit = false } = {}) => {
    const normalizedCode = String(code || '').trim().toUpperCase();
    if (!normalizedCode) return '';
    if (discountIsApplied(cart, normalizedCode)) {
      const savings = discountSavings(cart, normalizedCode);
      return savings > 0
        ? `${normalizedCode} applied — you saved ${formatMoney(savings)}.`
        : `${normalizedCode} applied to your bag.`;
    }

    const subtotal = cartPreDiscountSubtotal(cart);
    if (normalizedCode === defaultDiscountCode && discountThreshold > 0 && subtotal < discountThreshold) {
      const remaining = discountThreshold - subtotal;
      return Number(cart?.item_count || 0) > 0
        ? `${normalizedCode} saved — add ${formatMoney(remaining)} more to unlock 10% off.`
        : `${normalizedCode} is ready — add ${formatMoney(remaining)} to unlock 10% off.`;
    }

    return explicit ? `${normalizedCode} could not be applied to this bag.` : '';
  };

  const inferredDiscountCode = (cart) => {
    if (activeDiscountCode) return activeDiscountCode;
    if (attemptedDiscountCode) return attemptedDiscountCode;
    const codeRecord = cartDiscountRecords(cart).find((discount) => discount.applicable !== false);
    if (codeRecord?.code) return String(codeRecord.code).trim().toUpperCase();
    return cartDiscountApplications(cart).some((discount) =>
      String(discount.title || '').trim().toUpperCase() === defaultDiscountCode
    ) ? defaultDiscountCode : '';
  };

  const discountPanelMarkup = (cart, options = {}) => {
    const code = String(options.discountCode || inferredDiscountCode(cart)).trim().toUpperCase();
    const computedStatus = discountStatusForCart(cart, code);
    const status = options.discountStatus !== undefined
      ? options.discountStatus
      : (computedStatus || discountFeedback);
    return `
      <div class="cc-cart-discount" data-cart-discount>
        <label for="CartDrawerDiscountCode">Promo code</label>
        <div class="cc-cart-discount__controls">
          <input
            id="CartDrawerDiscountCode"
            type="text"
            value="${escapeHTML(code)}"
            placeholder="${escapeHTML(defaultDiscountCode)}"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            inputmode="text"
            data-cart-discount-input
          >
          <button type="button" data-cart-discount-apply>Apply</button>
        </div>
        <p class="cc-cart-discount__status" role="status" aria-live="polite" data-cart-discount-status>${escapeHTML(status)}</p>
      </div>`;
  };

  const requestDiscountUpdate = async (code) => {
    const response = await fetch(config.cartUpdateUrl || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.description || 'That promo code could not be checked.');
    return result;
  };

  const maybeReapplyStoredDiscount = async (cart) => {
    if (
      !activeDiscountCode
      || discountIsApplied(cart, activeDiscountCode)
      || cartPreDiscountSubtotal(cart) < discountThreshold
    ) return cart;
    let refreshedCart = cart;
    try {
      refreshedCart = await requestDiscountUpdate(activeDiscountCode);
    } catch {
      return cart;
    }
    if (!discountIsApplied(refreshedCart, activeDiscountCode)) {
      activeDiscountCode = '';
      writeSessionValue(discountCodeStorageKey, '');
    }
    return refreshedCart;
  };

  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((node) => {
      node.textContent = count;
    });
    $$('[data-cart-open]').forEach((trigger) => {
      trigger.setAttribute('aria-label', `Open bag with ${count} ${count === 1 ? 'item' : 'items'}`);
    });
    $$('[data-cart-menu-link]').forEach((link) => {
      link.textContent = `View bag (${count})`;
    });
    const titleCount = $('[data-cart-title-count]');
    if (titleCount) titleCount.textContent = `(${count})`;
  };

  const renderCart = (cart, options = {}) => {
    updateCount(cart.item_count);
    syncShippingProgress(cart.total_price, options);
    $$('product-display').forEach((display) => display.updateShippingPreview?.());
    if (!cartBody) return;

    if (!cart.items.length) {
      cartBody.innerHTML = `
        <div class="cc-cart-empty">
          <div>
            <p class="cc-kicker">Nothing here yet</p>
            <h3>Your bag is ready for something good.</h3>
            <a class="cc-button cc-button--yellow" href="${escapeHTML(config.allProductsUrl || '/collections/all')}">Shop everything</a>
          </div>
        </div>
        <div class="cc-cart-summary cc-cart-summary--empty">
          ${discountPanelMarkup(cart, options)}
        </div>`;
      return;
    }

    const items = cart.items.map((item, index) => {
      const itemTitle = displayTitle(item.product_title);
      const resizedImage = item.image
        ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=240`
        : '';
      const image = resizedImage
        ? `<a class="cc-cart-line__media" href="${escapeHTML(item.url)}" aria-label="View ${escapeHTML(itemTitle)}"><img class="cc-cart-line__image" src="${escapeHTML(resizedImage)}" alt="${escapeHTML(itemTitle)}" width="120" height="150"></a>`
        : '<div class="cc-cart-line__image" aria-hidden="true"></div>';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cc-cart-line__variant">${escapeHTML(item.variant_title)}</p>`
        : '';
      const properties = Object.entries(item.properties || {})
        .filter(([name, value]) => value && !String(name).startsWith('_'))
        .map(([name, value]) => `<li><span>${escapeHTML(name)}:</span> ${escapeHTML(value)}</li>`)
        .join('');
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.name
        ? `<p class="cc-cart-line__plan">${escapeHTML(item.selling_plan_allocation.selling_plan.name)}</p>`
        : '';
      const discountMap = new Map();
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const title = allocation.discount_application?.title;
        if (title) discountMap.set(title, Number(allocation.amount || 0));
      });
      (item.discounts || []).forEach((discount) => {
        if (discount.title) discountMap.set(discount.title, Number(discount.amount || 0));
      });
      const discounts = [...discountMap.entries()]
        .map(([title, amount]) => `<li><span>${escapeHTML(title)}</span><strong>−${formatMoney(amount)}</strong></li>`)
        .join('');
      const unitPrice = item.unit_price_measurement && item.unit_price
        ? `<small class="cc-cart-line__unit-price">${formatMoney(item.unit_price)}/${item.unit_price_measurement.reference_value !== 1 ? escapeHTML(item.unit_price_measurement.reference_value) : ''}${escapeHTML(item.unit_price_measurement.reference_unit)}</small>`
        : '';
      const linePrice = Number(item.original_line_price || 0) > Number(item.final_line_price || 0)
        ? `<span class="cc-cart-line__price"><s>${formatMoney(item.original_line_price)}</s><strong>${formatMoney(item.final_line_price)}</strong></span>`
        : `<span class="cc-cart-line__price">${formatMoney(item.final_line_price)}</span>`;
      return `
        <article class="cc-cart-line" data-cart-line="${index + 1}">
          ${image}
          <div class="cc-cart-line__details">
            <a class="cc-cart-line__title" href="${escapeHTML(item.url)}">${escapeHTML(itemTitle)}</a>
            ${variant}
            ${sellingPlan}
            ${properties ? `<ul class="cc-cart-line__properties" role="list">${properties}</ul>` : ''}
            ${unitPrice}
            ${discounts ? `<ul class="cc-cart-line__discounts" role="list">${discounts}</ul>` : '<div></div>'}
          </div>
          ${linePrice}
          <div class="cc-cart-line__quantity">
            <button type="button" data-cart-quantity="${index + 1}" data-cart-value="${Math.max(0, item.quantity - 1)}" aria-label="Decrease ${escapeHTML(itemTitle)} quantity">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-quantity="${index + 1}" data-cart-value="${item.quantity + 1}" aria-label="Increase ${escapeHTML(itemTitle)} quantity">+</button>
          </div>
          <button class="cc-cart-line__remove" type="button" data-cart-quantity="${index + 1}" data-cart-value="0" aria-label="Remove ${escapeHTML(itemTitle)} from bag">Remove</button>
        </article>`;
    }).join('');

    const cartDiscounts = (cart.cart_level_discount_applications || [])
      .map((discount) => `<div class="cc-cart-summary__row cc-cart-summary__row--discount"><span>${escapeHTML(discount.title)}</span><strong>−${formatMoney(discount.total_allocated_amount)}</strong></div>`)
      .join('');

    cartBody.innerHTML = `
      <div class="cc-cart-items">${items}</div>
      <div class="cc-cart-summary">
        ${cartDiscounts}
        ${discountPanelMarkup(cart, options)}
        <div class="cc-cart-summary__row"><span>Subtotal</span><strong>${formatMoney(cart.total_price)}</strong></div>
        <p class="cc-cart-summary__note">Shipping and taxes are calculated at checkout.</p>
        <a class="cc-cart-summary__checkout" href="${escapeHTML(config.checkoutUrl || '/checkout')}">Checkout · ${formatMoney(cart.total_price)}</a>
      </div>`;
  };

  const fetchCart = async (options = {}) => {
    const response = await fetch(`${config.cartUrl || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    let cart = await response.json();
    if (options.mutation) cart = await maybeReapplyStoredDiscount(cart);
    renderCart(cart, options);
    return cart;
  };

  const openCart = async (prefetchedCart, { opener } = {}) => {
    if (!config.cartDrawer || !cartLayer) {
      window.location.href = config.cartUrl || '/cart';
      return;
    }
    const requestedOpener = opener || document.activeElement;
    const visibleFallback = $$('[data-scroll-to-buy], [data-cart-open]').find(isOnscreen);
    const resolvedOpener = isOnscreen(requestedOpener) ? requestedOpener : visibleFallback;
    if (resolvedOpener instanceof HTMLElement && !cartLayer.contains(resolvedOpener)) {
      cartOpener = resolvedOpener;
    }
    cartLayer.hidden = false;
    setBodyLock();
    requestAnimationFrame(() => focusFirst($('[role="dialog"]', cartLayer) || cartLayer));
    try {
      if (prefetchedCart) renderCart(prefetchedCart);
      else await fetchCart();
    } catch (error) {
      cartBody.innerHTML = `<div class="cc-cart-empty"><p>We could not load your bag. <a href="${escapeHTML(config.cartUrl || '/cart')}">Open the bag page →</a></p></div>`;
      requestAnimationFrame(() => $('.cc-cart-empty a', cartBody)?.focus());
    }
  };

  const closeCart = () => {
    if (!cartLayer) return;
    cartLayer.hidden = true;
    setBodyLock();
    const fallback = $$('[data-cart-open], [data-scroll-to-buy]').find(isOnscreen);
    const target = isOnscreen(cartOpener) ? cartOpener : fallback;
    target?.focus({ preventScroll: true });
    cartOpener = null;
  };

  document.addEventListener('click', (event) => {
    const cartOpen = event.target.closest('[data-cart-open]');
    if (!cartOpen) return;
    event.preventDefault();
    openCart(undefined, { opener: cartOpen });
  });
  $$('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));

  let cartOperationQueue = Promise.resolve();
  const queueCartOperation = (operation) => {
    const queued = cartOperationQueue.then(operation, operation);
    cartOperationQueue = queued.catch(() => {});
    return queued;
  };

  const setDiscountControlsBusy = (container, busy) => {
    if (!container) return;
    container.classList.toggle('is-loading', busy);
    container.setAttribute('aria-busy', String(busy));
    $$('input, button', container).forEach((control) => { control.disabled = busy; });
  };

  const updateStaticDiscountControls = (code, status) => {
    $$('[data-cart-discount]').forEach((container) => {
      const input = $('[data-cart-discount-input]', container);
      const statusNode = $('[data-cart-discount-status]', container);
      if (input && code) input.value = code;
      if (statusNode) statusNode.textContent = status || '';
    });
  };

  const applyDiscountCode = async (rawCode, { opener, openDrawer = false } = {}) => {
    const code = String(rawCode || '').trim().toUpperCase();
    const container = opener?.closest?.('[data-cart-discount]');
    if (!code) {
      const message = 'Enter a promo code first.';
      const statusNode = $('[data-cart-discount-status]', container);
      if (statusNode) statusNode.textContent = message;
      showToast(message);
      return null;
    }

    attemptedDiscountCode = code;
    setDiscountControlsBusy(container, true);
    opener?.setAttribute?.('aria-busy', 'true');

    try {
      const cart = await queueCartOperation(() => requestDiscountUpdate(code));
      const status = discountStatusForCart(cart, code, { explicit: true });
      const keepCode = discountIsApplied(cart, code)
        || (
          code === defaultDiscountCode
          && discountThreshold > 0
          && cartPreDiscountSubtotal(cart) < discountThreshold
        );

      activeDiscountCode = keepCode ? code : '';
      discountFeedback = status;
      writeSessionValue(discountCodeStorageKey, activeDiscountCode);

      if (opener?.closest?.('.cc-cart-page')) {
        writeSessionValue(discountFeedbackStorageKey, status);
        window.location.reload();
        return cart;
      }

      if (cartBody) renderCart(cart, {
        mutation: true,
        discountCode: code,
        discountStatus: status,
      });
      updateStaticDiscountControls(code, status);
      if (openDrawer) await openCart(cart, { opener });
      showToast(status);
      return cart;
    } catch (error) {
      const message = error.message || 'That promo code could not be checked.';
      discountFeedback = message;
      updateStaticDiscountControls(code, message);
      showToast(message);
      return null;
    } finally {
      if (container?.isConnected) setDiscountControlsBusy(container, false);
      opener?.removeAttribute?.('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const offer = event.target.closest('[data-discount-offer]');
    if (offer) {
      event.preventDefault();
      applyDiscountCode(offer.dataset.discountCode || defaultDiscountCode, {
        opener: offer,
        openDrawer: true,
      });
      return;
    }

    const applyButton = event.target.closest('[data-cart-discount-apply]');
    if (!applyButton) return;
    event.preventDefault();
    const container = applyButton.closest('[data-cart-discount]');
    applyDiscountCode($('[data-cart-discount-input]', container)?.value, { opener: applyButton });
  });

  document.addEventListener('keydown', (event) => {
    const input = event.target.closest('[data-cart-discount-input]');
    if (!input || event.key !== 'Enter') return;
    event.preventDefault();
    applyDiscountCode(input.value, { opener: input });
  });

  updateStaticDiscountControls(activeDiscountCode, discountFeedback);

  let cartChangePending = false;
  cartBody?.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-quantity]');
    if (!control || cartChangePending) return;
    const requestedLine = Number(control.dataset.cartQuantity);
    const requestedValue = Number(control.dataset.cartValue);
    const focusKind = control.classList.contains('cc-cart-line__remove')
      ? 'remove'
      : requestedValue > Number(control.parentElement?.querySelector('span')?.textContent || 0)
        ? 'increase'
        : 'decrease';
    cartChangePending = true;
    cartBody.setAttribute('aria-busy', 'true');
    cartBody.inert = true;
    $$('[data-cart-quantity]', cartBody).forEach((button) => { button.disabled = true; });
    cartBody.classList.add('is-updating');
    try {
      const cart = await queueCartOperation(async () => {
        const response = await fetch(config.cartChangeUrl || '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: requestedLine,
            quantity: requestedValue,
          }),
        });
        if (!response.ok) throw new Error();
        const changedCart = await response.json();
        return maybeReapplyStoredDiscount(changedCart);
      });
      renderCart(cart, { mutation: true });
      requestAnimationFrame(() => {
        if (!cart.items.length) {
          $('.cc-cart-empty a', cartBody)?.focus();
          return;
        }
        const targetLine = Math.min(requestedLine, cart.items.length);
        const line = $(`[data-cart-line="${targetLine}"]`, cartBody);
        const controls = $$('[data-cart-quantity]', line);
        const target = focusKind === 'increase' ? controls[1] : controls[0];
        target?.focus({ preventScroll: true });
      });
      showToast('Bag updated.');
    } catch (error) {
      showToast('That quantity could not be updated.');
    } finally {
      cartChangePending = false;
      cartBody.removeAttribute('aria-busy');
      cartBody.inert = false;
      cartBody.classList.remove('is-updating');
      $$('[data-cart-quantity]', cartBody).forEach((button) => { button.disabled = false; });
    }
  });

  document.addEventListener('keydown', (event) => {
    const activeDialog = cartLayer && !cartLayer.hidden
      ? $('[role="dialog"]', cartLayer)
      : menuLayer && !menuLayer.hidden
        ? $('[role="dialog"]', menuLayer)
        : null;

    if (event.key === 'Tab' && activeDialog) {
      const focusable = getFocusable(activeDialog);
      if (!focusable.length) {
        event.preventDefault();
        activeDialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!activeDialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (event.key !== 'Escape') return;
    if (cartLayer && !cartLayer.hidden) closeCart();
    else if (menuLayer && !menuLayer.hidden) closeMenu();
    else if (searchPanel && !searchPanel.hidden) closeSearch();
  });

  /* AJAX add-to-cart */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-ajax-cart-form]');
    if (!form) return;
    if (event.submitter && !event.submitter.matches('[data-add-button]')) return;
    event.preventDefault();

    const submit = form.querySelector('[data-add-button]');
    const interactionOpener = isOnscreen(document.activeElement) ? document.activeElement : submit;
    submit?.classList.add('is-loading');
    if (submit) submit.disabled = true;

    let itemWasAdded = false;
    try {
      await queueCartOperation(async () => {
        const response = await fetch(config.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This item could not be added.');
        itemWasAdded = true;

        try {
          const cart = await fetchCart({ mutation: true });
          if (config.cartDrawer) await openCart(cart, { opener: interactionOpener });
          else showToast(`${displayTitle(result.product_title || 'Item')} added to your bag.`);
        } catch (refreshError) {
          showToast('Added to your bag. Refresh the bag to see the latest total.');
        }
      });
    } catch (error) {
      if (!itemWasAdded) showToast(error.message || 'This item could not be added.');
    } finally {
      submit?.classList.remove('is-loading');
      if (submit) {
        const display = form.closest('product-display');
        const currentVariant = display?.currentVariant?.();
        submit.disabled = Boolean(currentVariant && !currentVariant.available);
      }
    }
  });

  /* Quantity controls */
  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (!control) return;
    const quantity = control.closest('[data-quantity]');
    const input = $('[data-quantity-input]', quantity);
    if (!input) return;
    const step = Number(input.step || 1);
    const minimum = Number(input.min || 1);
    const next = Number(input.value || minimum) + (control.hasAttribute('data-quantity-plus') ? step : -step);
    input.value = Math.max(minimum, next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Product gallery and variants */
  class ProductDisplay extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.variants = parseJSON($('[data-product-variants]', this), []);
      this.media = parseJSON($('[data-product-media]', this), []);
      this.select = $('[data-variant-select]', this);
      this.slides = $$('[data-media-id]', $('[data-gallery-track]', this));
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.thumbScroller = $('.cc-product-thumbs', this);
      this.optionGroups = $$('[data-product-option]', this);
      this.optionButtons = $$('[data-option-button]', this);
      this.counterCurrent = $('[data-gallery-counter-current]', this);
      this.quantityInput = $('[data-quantity-input]', this);
      this.shippingPreview = $('[data-product-shipping-preview]', this);
      this.mobileBuybar = $('[data-mobile-buybar]', this);
      this.inlineAddButton = $('[data-add-button]', this);
      this.variantStatus = $('[data-variant-status]', this);
      this.mobileQuery = window.matchMedia('(max-width: 720px)');
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.lastOptionChange = null;
      this.projectedShippingTotal = null;

      this.select?.addEventListener('change', () => this.onVariantChange(), { signal });
      this.optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.selectOption(button);
        }, { signal });
      });
      this.quantityInput?.addEventListener('change', () => this.updateShippingPreview({ celebrateThreshold: true }), { signal });
      this.quantityInput?.addEventListener('input', () => this.updateShippingPreview({ celebrateThreshold: true }), { signal });
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(Number(thumb.dataset.mediaIndex)), { signal });
        thumb.addEventListener('keydown', (event) => this.onThumbKeydown(event), { signal });
      });
      $('[data-gallery-prev]', this)?.addEventListener('click', () => this.showMedia(this.activeIndex - 1), { signal });
      $('[data-gallery-next]', this)?.addEventListener('click', () => this.showMedia(this.activeIndex + 1), { signal });
      $('[data-scroll-to-buy]', this)?.addEventListener('click', () => {
        if (this.inlineAddButton && !this.inlineAddButton.disabled) this.inlineAddButton.click();
        else $('[id^="ProductBuy-"]', this)?.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'center',
        });
      }, { signal });

      const viewport = $('[data-gallery-viewport]', this);
      if (viewport) {
        const syncResponsiveGallery = () => {
          cancelAnimationFrame(this.gallerySyncRaf);
          this.gallerySyncRaf = requestAnimationFrame(() => {
            if (!this.isConnected || viewport.clientWidth === 0) return;
            viewport.scrollLeft = this.mobileQuery.matches
              ? this.activeIndex * viewport.clientWidth
              : 0;
          });
        };
        viewport.addEventListener('scroll', () => {
          if (!this.mobileQuery.matches) return;
          cancelAnimationFrame(this.galleryScrollRaf);
          this.galleryScrollRaf = requestAnimationFrame(() => {
            if (viewport.clientWidth === 0) return;
            const rawIndex = Math.round(viewport.scrollLeft / viewport.clientWidth);
            const index = Math.max(0, Math.min(this.slides.length - 1, rawIndex));
            if (index === this.activeIndex) return;
            this.setThumbState(index);
          });
        }, { passive: true, signal });
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener('change', syncResponsiveGallery, { signal });
        } else {
          this.galleryViewportChangeHandler = syncResponsiveGallery;
          this.mobileQuery.addListener(syncResponsiveGallery);
        }
        syncResponsiveGallery();
      }

      this.setThumbState(this.activeIndex);
      this.syncOptionButtons();
      this.updateShippingPreview();
      this.initMobileBuybar();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      if (this.galleryViewportChangeHandler) {
        this.mobileQuery?.removeListener?.(this.galleryViewportChangeHandler);
        this.galleryViewportChangeHandler = null;
      }
      if (this.mobileBuybarViewportHandler) {
        this.mobileQuery?.removeListener?.(this.mobileBuybarViewportHandler);
        this.mobileBuybarViewportHandler = null;
      }
      cancelAnimationFrame(this.galleryScrollRaf);
      cancelAnimationFrame(this.gallerySyncRaf);
      cancelAnimationFrame(this.mobileBuybarSyncRaf);
      this.galleryScrollRaf = 0;
      this.gallerySyncRaf = 0;
      this.mobileBuybarSyncRaf = 0;
    }

    showMedia(index) {
      if (!this.slides.length) return;
      const normalized = (index + this.slides.length) % this.slides.length;
      this.activeIndex = normalized;

      if (this.mobileQuery.matches) {
        const viewport = $('[data-gallery-viewport]', this);
        viewport?.scrollTo({
          left: normalized * viewport.clientWidth,
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      }
      this.setThumbState(normalized);
    }

    setThumbState(index, { scrollThumb = true } = {}) {
      this.activeIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        const inactive = slideIndex !== index;
        slide.classList.toggle('is-active', !inactive);
        slide.setAttribute('aria-hidden', String(inactive));
        slide.toggleAttribute('inert', inactive);
        if (inactive) {
          $('video', slide)?.pause();
          const externalVideo = $('iframe', slide);
          if (externalVideo?.contentWindow) {
            externalVideo.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            externalVideo.contentWindow.postMessage('{"method":"pause"}', '*');
          }
        }
      });
      this.thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
        thumb.setAttribute('tabindex', active ? '0' : '-1');
      });
      if (this.counterCurrent) this.counterCurrent.textContent = String(index + 1);
      const activeThumb = this.thumbs[index];
      if (scrollThumb && activeThumb && this.thumbScroller) {
        const target = activeThumb.offsetLeft - ((this.thumbScroller.clientWidth - activeThumb.offsetWidth) / 2);
        this.thumbScroller.scrollTo({
          left: Math.max(0, target),
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      }
    }

    onThumbKeydown(event) {
      const current = Number(event.currentTarget.dataset.mediaIndex);
      let next = current;
      if (event.key === 'ArrowLeft') next = current - 1;
      else if (event.key === 'ArrowRight') next = current + 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.thumbs.length - 1;
      else return;

      event.preventDefault();
      const normalized = (next + this.thumbs.length) % this.thumbs.length;
      this.showMedia(normalized);
      this.thumbs[normalized]?.focus();
    }

    currentVariant() {
      if (!this.select) return this.variants[0];
      return this.variants.find((item) => String(item.id) === String(this.select.value));
    }

    selectedOptionValues() {
      const currentOptions = this.currentVariant()?.options;
      const values = Array.isArray(currentOptions) ? [...currentOptions] : [];

      this.optionGroups.forEach((group) => {
        const position = Number(group.dataset.optionPosition || 1) - 1;
        const selectedButton = $('[data-option-button][data-option-selected]', group);
        if (selectedButton) values[position] = selectedButton.dataset.optionValue;
      });

      return values;
    }

    selectOption(button) {
      if (!this.select || !button || button.disabled) return;
      const position = Number(button.dataset.optionPosition || 1) - 1;
      const selectedOptions = this.selectedOptionValues();
      selectedOptions[position] = button.dataset.optionValue;
      const optionGroup = button.closest('[data-product-option]');
      this.lastOptionChange = {
        position,
        type: optionGroup?.dataset.optionType || 'text',
        value: button.dataset.optionValue,
      };

      const exactVariant = this.variants.find((candidate) =>
        candidate.available
        && Array.isArray(candidate.options)
        && candidate.options.length === selectedOptions.length
        && candidate.options.every((value, index) => String(value) === String(selectedOptions[index]))
      );
      const compatibleVariant = exactVariant || this.variants.find((candidate) =>
        candidate.available
        && Array.isArray(candidate.options)
        && candidate.options.every((value, index) =>
          index > position
          || selectedOptions[index] === undefined
          || String(value) === String(selectedOptions[index])
        )
      );
      if (!compatibleVariant) return;

      this.select.value = String(compatibleVariant.id);
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    optionValueIsAvailable(position, value, selectedOptions) {
      return this.variants.some((variant) =>
        variant.available
        && Array.isArray(variant.options)
        && String(variant.options[position]) === String(value)
        && variant.options.every((optionValue, optionPosition) =>
          optionPosition >= position
          || selectedOptions[optionPosition] === undefined
          || String(optionValue) === String(selectedOptions[optionPosition])
        )
      );
    }

    mediaIndexForVariant(variant) {
      if (!variant) return -1;
      if (this.lastOptionChange?.type !== 'color') return -1;

      const directMediaId = Number(variant.featured_media_id || 0);
      if (directMediaId) {
        return this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === directMediaId);
      }

      const { position, value } = this.lastOptionChange;
      const siblingWithMedia = this.variants.find((candidate) =>
        candidate.available
        && Array.isArray(candidate.options)
        && String(candidate.options[position]) === String(value)
        && Number(candidate.featured_media_id || 0) > 0
      );
      if (siblingWithMedia) {
        return this.slides.findIndex((slide) =>
          Number(slide.dataset.mediaId) === Number(siblingWithMedia.featured_media_id)
        );
      }

      const normalizedValue = String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      if (!normalizedValue) return -1;
      const mediaMatch = this.media.find((item) => {
        const normalizedAlt = String(item.alt || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        return normalizedAlt.includes(normalizedValue);
      });
      return mediaMatch ? this.slides.findIndex((slide) =>
        Number(slide.dataset.mediaId) === Number(mediaMatch.id)
      ) : -1;
    }

    syncOptionButtons(variant = this.currentVariant()) {
      if (!this.optionButtons.length || !Array.isArray(variant?.options)) return;
      const selectedOptions = [...variant.options];

      this.optionGroups.forEach((group) => {
        const position = Number(group.dataset.optionPosition || 1) - 1;
        const selectedValue = selectedOptions[position] ?? '';
        const selectedValueNode = $('[data-option-selected-value]', group);
        const selectedButton = this.optionButtons.find((button) =>
          Number(button.dataset.optionPosition || 1) - 1 === position
          && String(button.dataset.optionValue) === String(selectedValue)
        );
        if (selectedValueNode) {
          selectedValueNode.textContent = selectedButton?.dataset.optionDisplay || selectedValue;
        }
      });

      this.optionButtons.forEach((button) => {
        const position = Number(button.dataset.optionPosition || 1) - 1;
        const value = button.dataset.optionValue;
        const selected = String(value) === String(selectedOptions[position]);
        const available = this.optionValueIsAvailable(position, value, selectedOptions);

        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-active', selected);
        button.classList.toggle('is-unavailable', !available);
        button.toggleAttribute('data-option-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        button.setAttribute('aria-disabled', String(!available));
        button.disabled = !available;

        const unavailableLabel = $('[data-option-unavailable-label]', button);
        if (unavailableLabel) unavailableLabel.hidden = available;
      });
    }

    updateShippingPreview({ celebrateThreshold = false } = {}) {
      if (!this.shippingPreview || !freeShippingEnabled) return;
      const variant = this.currentVariant();
      if (!variant) return;

      const shippingContainer = this.shippingPreview.closest('[data-product-shipping]');
      const cartTotal = Number(observedCartTotal || 0);
      const threshold = freeShippingThreshold;
      const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
      const projectedTotal = cartTotal + (Number(variant.price || 0) * quantity);
      const previousProjectedTotal = this.projectedShippingTotal;
      this.projectedShippingTotal = projectedTotal;
      const remaining = Math.max(0, threshold - projectedTotal);
      const percentage = threshold > 0 ? Math.min(100, (projectedTotal / threshold) * 100) : 0;
      const shippingProgress = $('[data-product-shipping-progress]', shippingContainer);
      const shippingValue = $('[data-product-shipping-value]', shippingContainer);
      const shippingTrack = $('[data-product-shipping-track]', shippingContainer);
      const shippingFill = $('[data-product-shipping-fill]', shippingContainer);

      if (cartTotal >= threshold) {
        this.shippingPreview.innerHTML = '<strong>Free shipping is already unlocked.</strong>';
      } else if (remaining === 0) {
        this.shippingPreview.innerHTML = '<strong>This object unlocks free shipping.</strong>';
      } else {
        this.shippingPreview.innerHTML = `Add this and you’re <strong>${escapeHTML(formatMoney(remaining))}</strong> away from free shipping.`;
      }

      if (shippingProgress) {
        shippingProgress.classList.toggle('is-unlocked', remaining === 0);
      }
      if (shippingValue) {
        shippingValue.textContent = `${formatMoney(projectedTotal)} / ${formatMoney(threshold)} after adding`;
      }
      if (shippingFill) shippingFill.style.setProperty('--cc-shipping-progress', `${percentage}%`);
      if (shippingTrack) {
        shippingTrack.setAttribute('aria-valuenow', String(Math.round(percentage)));
        shippingTrack.setAttribute(
          'aria-valuetext',
          remaining === 0
            ? 'Free shipping unlocked after adding this item'
            : `${formatMoney(remaining)} remaining for free shipping after adding this item`
        );
      }

      if (
        celebrateThreshold
        && cartTotal < threshold
        && previousProjectedTotal !== null
        && previousProjectedTotal < threshold
        && projectedTotal >= threshold
      ) {
        celebrateFreeShipping();
      }
    }

    initMobileBuybar() {
      if (!this.mobileBuybar || !this.inlineAddButton) return;

      const footer = $('.cc-footer');
      const header = $('.cc-header');
      const { signal } = this.abortController;

      const syncVisibility = () => {
        this.mobileBuybarSyncRaf = 0;

        if (!this.mobileQuery.matches) {
          this.mobileBuybar.classList.remove('is-visible');
          this.mobileBuybar.setAttribute('aria-hidden', 'true');
          this.mobileBuybar.toggleAttribute('inert', true);
          return;
        }

        const buttonRect = this.inlineAddButton.getBoundingClientRect();
        const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
        const hasPassedInlineButton = buttonRect.bottom <= headerBottom + 8;
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const footerRect = footer?.getBoundingClientRect();
        const footerIsVisible = Boolean(
          footerRect
          && footerRect.top < viewportHeight
          && footerRect.bottom > 0
        );
        const productRect = this.getBoundingClientRect();
        const stillInsideProductSection = productRect.bottom > headerBottom + 72;
        const shouldShow = hasPassedInlineButton && stillInsideProductSection && !footerIsVisible;

        this.mobileBuybar.classList.toggle('is-visible', shouldShow);
        this.mobileBuybar.setAttribute('aria-hidden', String(!shouldShow));
        this.mobileBuybar.toggleAttribute('inert', !shouldShow);
      };

      const scheduleSync = () => {
        cancelAnimationFrame(this.mobileBuybarSyncRaf);
        this.mobileBuybarSyncRaf = requestAnimationFrame(syncVisibility);
      };

      window.addEventListener('scroll', scheduleSync, { passive: true, signal });
      window.addEventListener('resize', scheduleSync, { passive: true, signal });
      window.visualViewport?.addEventListener('resize', scheduleSync, { passive: true, signal });
      window.visualViewport?.addEventListener('scroll', scheduleSync, { passive: true, signal });

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', scheduleSync, { signal });
      } else {
        this.mobileBuybarViewportHandler = scheduleSync;
        this.mobileQuery.addListener(scheduleSync);
      }

      syncVisibility();
    }

    onVariantChange() {
      const variant = this.currentVariant();
      if (!variant) return;

      $$('[data-product-price], [data-product-price-mobile]', this).forEach((node) => {
        node.textContent = formatMoney(variant.price);
      });
      const compareAtPrice = Number(variant.compare_at_price || 0);
      const isOnSale = compareAtPrice > Number(variant.price || 0);
      $$('[data-product-compare-price], [data-product-compare-price-mobile]', this).forEach((node) => {
        node.textContent = isOnSale ? formatMoney(compareAtPrice) : '';
        node.hidden = !isOnSale;
      });
      const addPrice = $('[data-add-price]', this);
      if (addPrice) addPrice.textContent = formatMoney(variant.price);
      const addLabel = $('[data-add-label]', this);
      const addButton = $('[data-add-button]', this);
      const mobileAddLabel = $('[data-mobile-add-label]', this);
      const mobileAddPrice = $('[data-mobile-add-price]', this);
      const mobileAddButton = $('[data-scroll-to-buy]', this);
      if (addLabel) addLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
      if (addButton) addButton.disabled = !variant.available;
      if (mobileAddLabel) mobileAddLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
      if (mobileAddPrice) mobileAddPrice.textContent = formatMoney(variant.price);
      if (mobileAddButton) mobileAddButton.disabled = !variant.available;
      const paymentTerms = $('shopify-payment-terms', this);
      if (paymentTerms) paymentTerms.setAttribute('variant-id', String(variant.id));

      const mediaIndex = this.mediaIndexForVariant(variant);
      if (mediaIndex >= 0) this.showMedia(mediaIndex);

      this.syncOptionButtons(variant);
      this.updateShippingPreview();
      if (this.variantStatus) {
        const optionLabel = Array.isArray(variant.options) ? variant.options.join(', ') : '';
        this.variantStatus.textContent = `${optionLabel}${variant.available ? ' selected' : ' is sold out'}. ${formatMoney(variant.price)}.`;
      }

      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }
  }

  if (!customElements.get('product-display')) {
    customElements.define('product-display', ProductDisplay);
  }

  /* Synchronized cinematic homepage hero */
  const initializeVideoHero = (hero) => {
    if (!hero || hero.dataset.videoHeroReady === 'true') return;

    const desktopVideo = $('[data-video-kind="desktop"]', hero);
    const mobileVideo = $('[data-video-kind="mobile"]', hero);
    const heroVideos = [desktopVideo, mobileVideo].filter(Boolean);
    const scenes = $$('[data-video-cue]', hero);
    const cueTriggers = $$('[data-video-cue-trigger]', hero);
    const toggle = $('[data-video-toggle]', hero);
    const toggleIcon = $('[data-video-toggle-icon]', toggle);
    const toggleLabel = $('[data-video-toggle-label]', toggle);
    const status = $('[data-video-status]', hero);
    if (!desktopVideo || !scenes.length || !cueTriggers.length) return;

    hero.dataset.videoHeroReady = 'true';
    const controller = new AbortController();
    const { signal } = controller;
    const segmentDuration = Math.max(0.5, Number(hero.dataset.segmentDuration || 2));
    const segmentCount = Math.max(1, Number(hero.dataset.timelineSegments || cueTriggers.length || 4));
    const cycleDuration = segmentDuration * segmentCount;
    const mobileBreakpoint = window.matchMedia('(max-width: 720px)');

    const sceneData = scenes.map((scene, index) => {
      const start = index * segmentDuration;
      return { index, scene, start: Math.min(start, Math.max(0, cycleDuration - 0.01)) };
    });

    const timelineData = cueTriggers.map((trigger, index) => ({
      index,
      trigger,
      start: index * segmentDuration
    }));

    let activeVideo = null;
    let activeSceneIndex = -1;
    let activeSegmentIndex = -1;
    let pausedByUser = false;
    let motionOverride = false;
    let inView = true;
    let timelineTime = 0;
    let pendingTimelineSeek = null;
    let animationFrame = 0;
    let visibilityObserver = null;

    heroVideos.forEach((video) => {
      prepareSilentInlineVideo(video, { loop: true });
      video.pause();
      video.classList.remove('is-active');
    });

    const normalizeTimelineTime = (time) => {
      const normalized = Number(time || 0) % cycleDuration;
      return normalized < 0 ? normalized + cycleDuration : normalized;
    };

    const syncTimelineTimeFromVideo = () => {
      if (!activeVideo) return false;
      const duration = Number(activeVideo.duration || 0);
      const currentTime = Number(activeVideo.currentTime || 0);
      if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(currentTime)) return false;
      timelineTime = normalizeTimelineTime((currentTime / duration) * cycleDuration);
      return true;
    };

    const setToggleState = () => {
      const playing = activeVideo && !activeVideo.paused && !activeVideo.ended;
      if (toggleIcon) toggleIcon.textContent = playing ? 'Ⅱ' : '▶';
      if (toggleLabel) toggleLabel.textContent = playing ? 'Pause' : 'Play';
      if (toggle) toggle.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} hero video`);
      hero.classList.toggle('is-playing', Boolean(playing));
    };

    const activateScene = (index, { announce = false, force = false } = {}) => {
      const requestedScene = sceneData.find((item) => item.index === index) || sceneData[0];
      if (!requestedScene) return;
      index = requestedScene.index;
      if (!force && index === activeSceneIndex) return;
      activeSceneIndex = index;

      scenes.forEach((scene, sceneIndex) => {
        const active = sceneIndex === index;
        scene.classList.toggle('is-active', active);
        scene.setAttribute('aria-hidden', String(!active));
        scene.toggleAttribute('inert', !active);
      });

      if (announce && status) {
        const heading = $('h1, h2', requestedScene.scene)?.textContent?.trim() || `Scene ${index + 1}`;
        status.textContent = `Scene ${index + 1}: ${heading}`;
      }
    };

    const activateSegment = (index, { announce = false } = {}) => {
      const requestedSegment = timelineData.find((item) => item.index === index) || timelineData[0];
      if (!requestedSegment) return;
      index = requestedSegment.index;
      if (index !== activeSegmentIndex) {
        activeSegmentIndex = index;
        cueTriggers.forEach((cue, cueIndex) => {
          const active = cueIndex === index;
          cue.classList.toggle('is-active', active);
          cue.setAttribute('aria-pressed', String(active));
        });
      }

      if (announce && status) {
        status.textContent = `Hero segment ${index + 1} of ${segmentCount}`;
      }
    };

    const sceneForTime = (time) => {
      let currentScene = sceneData[0];
      sceneData.forEach((item) => {
        if (time + 0.001 >= item.start) currentScene = item;
      });
      return currentScene;
    };

    const syncTimeline = () => {
      const time = normalizeTimelineTime(timelineTime);
      const currentSegmentIndex = Math.min(segmentCount - 1, Math.floor(time / segmentDuration));
      const segmentStart = currentSegmentIndex * segmentDuration;
      const progress = Math.max(0, Math.min(1, (time - segmentStart) / segmentDuration));

      activateSegment(currentSegmentIndex);
      const currentScene = sceneForTime(time);
      if (currentScene) activateScene(currentScene.index);

      cueTriggers.forEach((cue, cueIndex) => {
        const cueBar = $('i', cue);
        if (!cueBar) return;
        const cueProgress = cueIndex < currentSegmentIndex ? 1 : cueIndex > currentSegmentIndex ? 0 : progress;
        cueBar.style.setProperty('--cc-cue-progress', cueProgress);
      });
    };

    const seekVideoForTimeline = () => {
      if (!activeVideo) return;
      const duration = Number(activeVideo.duration || 0);
      if (!Number.isFinite(duration) || duration <= 0) {
        pendingTimelineSeek = normalizeTimelineTime(timelineTime);
        return;
      }
      const targetTime = (normalizeTimelineTime(timelineTime) / cycleDuration) * duration;
      pendingTimelineSeek = null;
      activeVideo.currentTime = Math.min(targetTime, Math.max(0, duration - 0.001));
    };

    const setTimelineTime = (time, { announce = false, seekVideo = false } = {}) => {
      timelineTime = normalizeTimelineTime(time);
      syncTimeline();
      if (seekVideo) seekVideoForTimeline();
      if (announce) activateSegment(Math.min(segmentCount - 1, Math.floor(timelineTime / segmentDuration)), { announce: true });
    };

    const stopTimeline = () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = 0;
    };

    const runTimeline = () => {
      stopTimeline();
      const tick = () => {
        syncTimelineTimeFromVideo();
        syncTimeline();
        if (activeVideo && !activeVideo.paused) animationFrame = requestAnimationFrame(tick);
        else animationFrame = 0;
      };
      animationFrame = requestAnimationFrame(tick);
    };

    const shouldPlay = () =>
      Boolean(activeVideo)
      && !pausedByUser
      && (!reducedMotion.matches || motionOverride)
      && inView
      && !document.hidden;

    const syncPlayback = () => {
      if (!activeVideo) return;
      if (shouldPlay()) {
        const playPromise = activeVideo.play();
        if (playPromise?.catch) playPromise.catch(() => setToggleState());
      } else {
        activeVideo.pause();
      }
      setToggleState();
    };

    const setActiveVideo = () => {
      const nextVideo = mobileBreakpoint.matches && mobileVideo ? mobileVideo : desktopVideo;
      if (nextVideo === activeVideo) return;

      if (activeVideo) {
        syncTimelineTimeFromVideo();
        activeVideo.pause();
        activeVideo.classList.remove('is-active');
      }

      const targetVideo = nextVideo;
      activeVideo = targetVideo;
      activeVideo.classList.add('is-active');
      heroVideos.forEach((video) => {
        if (video !== activeVideo) {
          video.pause();
          video.classList.remove('is-active');
        }
      });

      const syncTime = () => {
        if (activeVideo !== targetVideo) return;
        if (pendingTimelineSeek !== null) timelineTime = pendingTimelineSeek;
        seekVideoForTimeline();
        syncTimelineTimeFromVideo();
        syncTimeline();
        syncPlayback();
      };

      syncPlayback();
      if (targetVideo.readyState >= 1) syncTime();
      else targetVideo.addEventListener('loadedmetadata', syncTime, { once: true, signal });
    };

    cueTriggers.forEach((cue, index) => {
      cue.addEventListener('click', () => {
        const selectedSegment = timelineData[index];
        if (!selectedSegment) return;
        setTimelineTime(selectedSegment.start, { announce: true, seekVideo: true });
        syncPlayback();
      }, { signal });
    });

    toggle?.addEventListener('click', () => {
      if (!activeVideo) return;
      if (activeVideo.paused || activeVideo.ended) {
        pausedByUser = false;
        motionOverride = true;
      } else {
        pausedByUser = true;
        motionOverride = false;
      }
      syncPlayback();
    }, { signal });

    heroVideos.forEach((video) => {
      const resyncFromVideo = () => {
        if (video !== activeVideo) return;
        if (pendingTimelineSeek !== null) {
          timelineTime = pendingTimelineSeek;
          seekVideoForTimeline();
        }
        syncTimelineTimeFromVideo();
        syncTimeline();
      };
      ['loadedmetadata', 'durationchange', 'seeking', 'seeked', 'timeupdate'].forEach((eventName) => {
        video.addEventListener(eventName, resyncFromVideo, { signal });
      });
      video.addEventListener('play', () => {
        if (video === activeVideo) {
          syncTimelineTimeFromVideo();
          setToggleState();
          runTimeline();
        }
      }, { signal });
      video.addEventListener('pause', () => {
        setToggleState();
        stopTimeline();
        syncTimelineTimeFromVideo();
        syncTimeline();
      }, { signal });
    });

    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting && entry.intersectionRatio > 0.08;
        syncPlayback();
      }, { threshold: [0, 0.08, 0.25] });
      visibilityObserver.observe(hero);
    }

    document.addEventListener('visibilitychange', syncPlayback, { signal });
    const onBreakpointChange = () => setActiveVideo();
    if (mobileBreakpoint.addEventListener) {
      mobileBreakpoint.addEventListener('change', onBreakpointChange, { signal });
    } else {
      mobileBreakpoint.addListener(onBreakpointChange);
    }

    const onMotionChange = () => {
      if (reducedMotion.matches) {
        motionOverride = false;
        pausedByUser = true;
        if (activeVideo) activeVideo.currentTime = 0;
        setTimelineTime(0);
      }
      syncPlayback();
    };
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange, { signal });
    else reducedMotion.addListener(onMotionChange);

    hero.addEventListener('shopify:block:select', (event) => {
      const scene = event.target.closest?.('[data-video-cue]');
      if (!scene) return;
      const index = Number(scene.dataset.cueIndex || 0);
      const time = index * segmentDuration;
      setTimelineTime(time, { seekVideo: true });
      activateScene(index, { announce: true, force: true });
    }, { signal });

    hero.__ccVideoHeroCleanup = () => {
      stopTimeline();
      visibilityObserver?.disconnect();
      controller.abort();
      if (!reducedMotion.addEventListener) reducedMotion.removeListener(onMotionChange);
      if (!mobileBreakpoint.addEventListener) mobileBreakpoint.removeListener(onBreakpointChange);
      heroVideos.forEach((video) => video.pause());
      delete hero.dataset.videoHeroReady;
      delete hero.__ccVideoHeroCleanup;
    };

    activateScene(sceneData[0].index);
    activateSegment(0);
    syncTimeline();
    setActiveVideo();
    setToggleState();
  };

  const initializeVideoHeroes = (scope = document) => {
    const heroes = [
      ...(scope.matches?.('[data-video-hero]') ? [scope] : []),
      ...$$('[data-video-hero]', scope),
    ];
    heroes.forEach(initializeVideoHero);
  };

  const destroyVideoHeroes = (scope) => {
    const heroes = [
      ...(scope.matches?.('[data-video-hero]') ? [scope] : []),
      ...$$('[data-video-hero]', scope),
    ];
    heroes.forEach((hero) => hero.__ccVideoHeroCleanup?.());
  };

  /* Collection hero video */
  const initializeCollectionVideoHero = (hero) => {
    if (!hero || hero.dataset.collectionVideoHeroReady === 'true') return;

    const video = $('[data-collection-video-kind="desktop"]', hero);
    if (!video) return;

    hero.dataset.collectionVideoHeroReady = 'true';
    const controller = new AbortController();
    const { signal } = controller;
    let inView = true;
    let visibilityObserver = null;

    prepareSilentInlineVideo(video, { loop: true });
    video.autoplay = true;

    const autoplayAllowed = () => !reducedMotion.matches && !networkConnection?.saveData;
    const shouldPlay = () =>
      inView
      && !document.hidden
      && autoplayAllowed();

    const syncPlayback = () => {
      if (shouldPlay()) {
        const promise = video.play();
        promise?.catch?.(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    };

    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting && entry.intersectionRatio > 0.08;
        syncPlayback();
      }, { threshold: [0, 0.08, 0.3] });
      visibilityObserver.observe(hero);
    }

    document.addEventListener('visibilitychange', syncPlayback, { signal });

    const onMotionChange = () => {
      if (reducedMotion.matches) {
        video.pause();
        video.currentTime = 0;
      }
      syncPlayback();
    };
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange, { signal });
    else reducedMotion.addListener(onMotionChange);

    hero.__ccCollectionVideoHeroCleanup = () => {
      visibilityObserver?.disconnect();
      controller.abort();
      if (!reducedMotion.addEventListener) reducedMotion.removeListener(onMotionChange);
      video.pause();
      delete hero.dataset.collectionVideoHeroReady;
      delete hero.__ccCollectionVideoHeroCleanup;
    };

    syncPlayback();
  };

  const initializeCollectionVideoHeroes = (scope = document) => {
    const heroes = [
      ...(scope.matches?.('[data-collection-video-hero]') ? [scope] : []),
      ...$$('[data-collection-video-hero]', scope),
    ];
    heroes.forEach(initializeCollectionVideoHero);
  };

  const destroyCollectionVideoHeroes = (scope) => {
    const heroes = [
      ...(scope.matches?.('[data-collection-video-hero]') ? [scope] : []),
      ...$$('[data-collection-video-hero]', scope),
    ];
    heroes.forEach((hero) => hero.__ccCollectionVideoHeroCleanup?.());
  };

  /* Video play controls */
  const initializeVideoStage = (stage) => {
    if (!stage || stage.dataset.videoStageReady === 'true') return;
    const video = $('video', stage);
    const play = $('[data-video-play]', stage);
    if (!video || !play) return;
    const controller = new AbortController();
    const { signal } = controller;
    const label = $('[data-video-label]', play);
    const icon = $('[data-video-icon]', play);
    const requestsAutoplay = stage.hasAttribute('data-video-autoplay');
    let visibilityObserver;
    let isVisible = false;
    let pausedByUser = false;
    let manualPlayback = false;
    stage.dataset.videoStageReady = 'true';
    video.controls = false;

    const autoplayAllowed = () =>
      requestsAutoplay
      && !reducedMotion.matches
      && !networkConnection?.saveData;

    const setControlState = () => {
      const isPlaying = !video.paused && !video.ended;
      play.dataset.playing = String(isPlaying);
      if (icon) icon.textContent = isPlaying ? 'Ⅱ' : '▶';
      if (label) label.textContent = isPlaying ? 'Pause product video' : 'Play product video';
      play.setAttribute('aria-pressed', String(isPlaying));
    };

    const requestPlayback = (manual = false) => {
      prepareSilentInlineVideo(video, { loop: video.loop, controls: false });
      manualPlayback = manual;
      const promise = video.play();
      promise?.catch?.(setControlState);
    };

    const syncAutoplay = () => {
      video.loop = autoplayAllowed();
      if (!isVisible || document.hidden) {
        if (!video.paused) video.pause();
        return;
      }
      if (manualPlayback) {
        return;
      }
      if (!autoplayAllowed() || pausedByUser) {
        if (!video.paused && requestsAutoplay) video.pause();
        return;
      }
      requestPlayback(false);
    };

    play.addEventListener('click', () => {
      if (video.paused || video.ended) {
        pausedByUser = false;
        requestPlayback(true);
      } else {
        pausedByUser = true;
        manualPlayback = false;
        video.pause();
      }
    }, { signal });
    video.addEventListener('play', setControlState, { signal });
    video.addEventListener('pause', setControlState, { signal });
    video.addEventListener('ended', () => {
      manualPlayback = false;
      setControlState();
    }, { signal });
    document.addEventListener('visibilitychange', syncAutoplay, { signal });

    if (requestsAutoplay && 'IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(([entry]) => {
        isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
        syncAutoplay();
      }, { threshold: [0, 0.35, 0.7] });
      visibilityObserver.observe(stage);
    }

    const onMotionChange = () => {
      if (reducedMotion.matches) {
        if (!manualPlayback) {
          video.pause();
          video.currentTime = 0;
          pausedByUser = true;
        }
      } else {
        pausedByUser = false;
      }
      syncAutoplay();
    };
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange, { signal });
    else reducedMotion.addListener(onMotionChange);

    if (!('IntersectionObserver' in window)) {
      isVisible = true;
      syncAutoplay();
    }

    setControlState();
    stage.__ccVideoStageCleanup = () => {
      video.pause();
      visibilityObserver?.disconnect();
      controller.abort();
      if (!reducedMotion.addEventListener) reducedMotion.removeListener(onMotionChange);
      delete stage.dataset.videoStageReady;
      delete stage.__ccVideoStageCleanup;
    };
  };

  const initializeVideoStages = (scope = document) => {
    const stages = [
      ...(scope.matches?.('[data-video-stage]') ? [scope] : []),
      ...$$('[data-video-stage]', scope),
    ];
    stages.forEach(initializeVideoStage);
  };

  const destroyVideoStages = (scope) => {
    const stages = [
      ...(scope.matches?.('[data-video-stage]') ? [scope] : []),
      ...$$('[data-video-stage]', scope),
    ];
    stages.forEach((stage) => stage.__ccVideoStageCleanup?.());
  };

  /* Creator stories autoplay silently, loop, and pause when they leave the viewport. */
  const initializeCreatorVideos = (scope = document) => {
    const videos = [
      ...(scope.matches?.('.cc-creator-story__video') ? [scope] : []),
      ...$$('.cc-creator-story__video', scope),
    ];

    videos.forEach((video) => {
      video.__ccCreatorVideoCleanup?.();
      let visibilityObserver;
      const controller = new AbortController();
      const { signal } = controller;
      const canObserveVisibility = 'IntersectionObserver' in window;
      let inView = !canObserveVisibility;

      prepareSilentInlineVideo(video, { loop: true, controls: false });
      video.autoplay = true;

      const syncPlayback = () => {
        if (inView && !document.hidden && !reducedMotion.matches) {
          video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      };

      document.addEventListener('visibilitychange', syncPlayback, { signal });
      const onMotionChange = () => syncPlayback();
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange, { signal });
      else reducedMotion.addListener(onMotionChange);
      if (canObserveVisibility) {
        visibilityObserver = new IntersectionObserver(([entry]) => {
          inView = entry.isIntersecting;
          syncPlayback();
        }, { rootMargin: '200px 0px', threshold: 0 });
        visibilityObserver.observe(video);
      }

      syncPlayback();
      video.__ccCreatorVideoCleanup = () => {
        video.pause();
        visibilityObserver?.disconnect();
        controller.abort();
        if (!reducedMotion.addEventListener) reducedMotion.removeListener(onMotionChange);
        delete video.__ccCreatorVideoCleanup;
      };
    });
  };

  const destroyCreatorVideos = (scope) => {
    const videos = [
      ...(scope.matches?.('.cc-creator-story__video') ? [scope] : []),
      ...$$('.cc-creator-story__video', scope),
    ];
    videos.forEach((video) => video.__ccCreatorVideoCleanup?.());
  };

  /* Shopify-powered related products with curated/collection fallback. */
  const initializeProductRecommendations = (scope = document) => {
    const sections = [
      ...(scope.matches?.('[data-product-recommendations]') ? [scope] : []),
      ...$$('[data-product-recommendations][data-recommendations-pending="true"]', scope),
    ];

    sections.forEach((section) => {
      if (section.dataset.recommendationsLoading === 'true') return;
      const url = section.dataset.recommendationsUrl;
      if (!url) return;
      section.dataset.recommendationsLoading = 'true';

      fetch(url, { headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error('Recommendations unavailable');
          return response.text();
        })
        .then((html) => {
          const template = document.createElement('template');
          template.innerHTML = html.trim();
          const replacement = $('[data-product-recommendations]', template.content);
          const replacementHasCards = Boolean(replacement && $('.cc-product-card', replacement));
          const currentHasCards = Boolean($('.cc-product-card', section));

          if (!replacementHasCards) {
            section.dataset.recommendationsPending = 'false';
            if (currentHasCards) {
              section.hidden = false;
            } else {
              section.remove();
            }
            return;
          }

          replacement.dataset.recommendationsPending = 'false';
          replacement.hidden = false;
          section.replaceWith(replacement);
          initializeReveals(replacement);
        })
        .catch(() => {
          section.dataset.recommendationsPending = 'false';
          if ($('.cc-product-card', section)) {
            section.hidden = false;
          } else {
            section.remove();
          }
        })
        .finally(() => {
          delete section.dataset.recommendationsLoading;
        });
    });
  };

  /* Shared reveal language */
  let revealObserver;
  const initializeReveals = (scope = document) => {
    const revealItems = [
      ...(scope.matches?.('[data-reveal]') ? [scope] : []),
      ...$$('[data-reveal]', scope),
    ].filter((item) => !item.classList.contains('is-revealed'));
    if (!revealItems.length) return;

    document.documentElement.classList.add('cc-reveal-ready');
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-revealed'));
    } else {
      revealObserver ||= new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          });
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      revealItems.forEach((item) => revealObserver.observe(item));
    }
  };

  const destroyReveals = (scope) => {
    const revealItems = [
      ...(scope.matches?.('[data-reveal]') ? [scope] : []),
      ...$$('[data-reveal]', scope),
    ];
    revealItems.forEach((item) => revealObserver?.unobserve(item));
  };

  const initializeSection = (scope = document) => {
    menuLayer = $('[data-menu-layer]');
    searchPanel = $('[data-search-panel]');
    initializeHeroHeader();
    initializeVideoHeroes(scope);
    initializeVideoStages(scope);
    initializeCreatorVideos(scope);
    initializeCollectionVideoHeroes(scope);
    initializeHoverImages(scope);
    initializeProductRecommendations(scope);
    initializeReveals(scope);
  };

  const destroySection = (scope) => {
    const containsNode = (node) => Boolean(node) && (scope === node || scope.contains?.(node));
    if (containsNode(menuLayer)) {
      menuLayer.hidden = true;
      menuOpener = null;
    }
    if (containsNode(searchPanel)) {
      searchPanel.hidden = true;
      searchOpener = null;
    }
    setBodyLock();
    destroyVideoHeroes(scope);
    destroyVideoStages(scope);
    destroyCreatorVideos(scope);
    destroyCollectionVideoHeroes(scope);
    destroyReveals(scope);
    queueMicrotask(initializeHeroHeader);
  };

  initializeSection();
  document.addEventListener('shopify:section:load', (event) => initializeSection(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySection(event.target));

  /* Preserve a cart-page threshold crossing through its native reload. */
  const cartForm = $('.cc-cart-form');
  cartForm?.addEventListener('submit', (event) => {
    if (!freeShippingEnabled || event.submitter?.name === 'checkout') return;
    try {
      sessionStorage.setItem('cc:shipping-pending-update', JSON.stringify({
        total: observedCartTotal,
        time: Date.now(),
      }));
    } catch (error) {
      /* Storage can be unavailable in strict privacy modes. */
    }
  });

  try {
    const pendingUpdate = JSON.parse(sessionStorage.getItem('cc:shipping-pending-update') || 'null');
    sessionStorage.removeItem('cc:shipping-pending-update');
    if (
      freeShippingEnabled
      && pendingUpdate
      && Date.now() - Number(pendingUpdate.time || 0) < 15000
      && Number(pendingUpdate.total || 0) < freeShippingThreshold
      && observedCartTotal >= freeShippingThreshold
    ) {
      celebrateFreeShipping();
    }
  } catch (error) {
    /* Ignore expired or blocked session storage. */
  }

  syncShippingProgress(observedCartTotal);
})();
